import type { DomainEventDefinition, JsonValue, RecordDefinition, Transaction } from "@e308/core";
import type {
  MatchEventPayload,
  NewBorderEventPayload,
  SeatEventPayload,
  SeatModel,
} from "./content-types.js";
import { type Micro, microFromString } from "./exact-numbers.js";
import {
  borderIsActive,
  cloneLifecycle,
  contractedEdges,
  type LockedMatchSetup,
  OVERTIME_LIMIT_MS,
  outcomeFor,
  reconcileBorders,
} from "./lifecycle-state.js";
import type { MatchLifecycleState } from "./lifecycle-types.js";
import { advanceLogistics } from "./logistics-runtime.js";
import { emptyTroops, startTravel } from "./logistics-state.js";
import type { LogisticsState, TroopComposition } from "./logistics-types.js";
import { UNIT_IDS } from "./logistics-types.js";
import type { ManufacturingState } from "./manufacturing-types.js";
import { effectMultiplier, effectsForResearch } from "./research-effects.js";
import { endResearch } from "./research-state.js";
import { unitResource } from "./seat-resources.js";

export interface LifecycleEvents {
  readonly seatEliminated: DomainEventDefinition<SeatEventPayload>;
  readonly newBorder: DomainEventDefinition<NewBorderEventPayload>;
  readonly overtimeStarted: DomainEventDefinition<JsonValue>;
  readonly matchEnded: DomainEventDefinition<MatchEventPayload>;
}

export function runOvertimeStep(
  transaction: Transaction<Micro>,
  seats: readonly SeatModel[],
  lifecycleDefinition: RecordDefinition<MatchLifecycleState & JsonValue>,
  setup: LockedMatchSetup,
  elapsedMs: number,
  overtimeStarted: DomainEventDefinition<JsonValue>,
): void {
  let state = transaction.getRecord(lifecycleDefinition) as MatchLifecycleState;
  if (state.status === "ended") return;
  const nowMs = transaction.gameTimeMs();
  const living = livingSeats(transaction, seats);
  if (
    state.status === "active" &&
    nowMs >= state.timeLimitMs &&
    outcomeFor(living, state.mode, setup.teamBySeat) === null
  ) {
    state = cloneLifecycle({
      ...state,
      status: "overtime",
      overtimeStartedAtMs: state.timeLimitMs,
    });
    transaction.setRecord(lifecycleDefinition, state as MatchLifecycleState & JsonValue);
    transaction.emit(overtimeStarted, {});
  }
  if (state.status !== "overtime" || state.overtimeStartedAtMs === null) return;
  const intervalStart = nowMs - elapsedMs;
  const overtimeEnd = state.overtimeStartedAtMs + OVERTIME_LIMIT_MS;
  const overlapStart = Math.max(intervalStart, state.overtimeStartedAtMs);
  const overlapEnd = Math.min(nowMs, overtimeEnd);
  if (overlapEnd <= overlapStart) return;
  const damage = (BigInt(overlapEnd - overlapStart) * 20_000n) as Micro;
  for (const model of seats) {
    const headquarters = transaction.get(model.resources.headquarters);
    if (headquarters > 0n)
      transaction.set(
        model.resources.headquarters,
        (headquarters > damage ? headquarters - damage : 0n) as Micro,
      );
  }
}

export function runLifecycleCleanup(
  transaction: Transaction<Micro>,
  seats: readonly SeatModel[],
  lifecycleDefinition: RecordDefinition<MatchLifecycleState & JsonValue>,
  setup: LockedMatchSetup,
  stepMs: number,
  events: LifecycleEvents,
): void {
  let state = transaction.getRecord(lifecycleDefinition) as MatchLifecycleState;
  if (state.status === "ended") return;
  const nowMs = transaction.gameTimeMs();
  const already = new Set(state.eliminated.map((entry) => entry.seatId));
  const newlyEliminated = seats.filter(
    (model) => !already.has(model.seat.id) && transaction.get(model.resources.headquarters) <= 0n,
  );
  if (newlyEliminated.length > 0) {
    state = cloneLifecycle({
      ...state,
      eliminated: [
        ...state.eliminated,
        ...newlyEliminated.map((model) => ({ seatId: model.seat.id, atGameMs: nowMs })),
      ],
    });
    for (const model of newlyEliminated) {
      clearEliminatedSeat(transaction, model);
      transaction.emit(events.seatEliminated, { seatId: model.seat.id });
    }
    const eliminated = new Set(state.eliminated.map((entry) => entry.seatId));
    const nextEdges = contractedEdges(setup.originalEdges, eliminated);
    const reconciled = reconcileBorders(state, nextEdges, nowMs);
    state = reconciled.state;
    reconcileSurvivingLogistics(transaction, seats, state, stepMs);
    clearInactiveBorders(transaction, seats, state);
    for (const border of reconciled.added) {
      transaction.emit(events.newBorder, {
        seatId: border.left,
        borderId: border.right,
        fightingStartsAtMs: border.fightingStartsAtMs,
      });
      transaction.emit(events.newBorder, {
        seatId: border.right,
        borderId: border.left,
        fightingStartsAtMs: border.fightingStartsAtMs,
      });
    }
  }
  const outcome = outcomeFor(livingSeats(transaction, seats), state.mode, setup.teamBySeat);
  const overtimeExpired =
    state.status === "overtime" &&
    state.overtimeStartedAtMs !== null &&
    nowMs >= state.overtimeStartedAtMs + OVERTIME_LIMIT_MS;
  if (outcome || overtimeExpired) {
    const terminal = outcome ?? { kind: "draw" as const, winnerId: null };
    state = cloneLifecycle({
      ...state,
      status: "ended",
      endedAtMs: nowMs,
      outcome: terminal,
    });
    transaction.emit(events.matchEnded, {
      outcome: terminal.kind,
      winnerId: terminal.winnerId,
    });
  }
  transaction.setRecord(lifecycleDefinition, state as MatchLifecycleState & JsonValue);
}

function clearEliminatedSeat(transaction: Transaction<Micro>, model: SeatModel): void {
  for (const id of UNIT_IDS) transaction.set(unitResource(model.resources, id), 0n as Micro);
  const logistics = transaction.getRecord(model.logistics) as LogisticsState;
  transaction.setRecord(model.logistics, {
    ...logistics,
    borders: Object.fromEntries(
      model.borderIds.map((id) => [id, { troops: emptyTroops(), standingOrder: "hold" }]),
    ),
    transits: [],
    autoReinforce: [],
    ...(logistics.reinforcement
      ? {
          reinforcement: {
            ...logistics.reinforcement,
            shares: { "guard-robot": {}, "raider-robot": {}, "siege-gun": {} },
            gathered: {},
            modes: {},
            ...(logistics.reinforcement.timers ? { timers: {} } : {}),
          },
        }
      : {}),
  });
  const combat = transaction.getRecord(model.combat);
  transaction.setRecord(model.combat, {
    borders: Object.fromEntries(
      model.borderIds.map((id) => [
        id,
        {
          ...combat.borders[id],
          shieldCharge: "0",
          requestedTarget: "headquarters",
          effectiveTarget: "headquarters",
          lastAttack: null,
        },
      ]),
    ),
  });
  const manufacturing = transaction.getRecord(model.manufacturing) as ManufacturingState;
  transaction.setRecord(model.manufacturing, {
    ...manufacturing,
    construction: [],
    restoration: [],
    assembly: [],
  });
  transaction.setRecord(model.research, endResearch(transaction.getRecord(model.research)));
  for (const allocation of [model.targeting, model.supplyAllocation, model.shieldAllocation])
    for (const id of model.borderIds) transaction.setAllocation(allocation.id, id, 0n as Micro);
}

function reconcileSurvivingLogistics(
  transaction: Transaction<Micro>,
  seats: readonly SeatModel[],
  lifecycle: MatchLifecycleState,
  stepMs: number,
): void {
  for (const model of seats) {
    if (transaction.get(model.resources.headquarters) <= 0n) continue;
    const valid = new Set(
      model.borderIds.filter((id) => borderIsActive(lifecycle, model.seat.id, id)),
    );
    const multiplier = effectMultiplier(
      effectsForResearch(transaction.getRecord(model.research)),
      "military-travel-time",
    );
    let state = advanceLogistics(
      transaction.getRecord(model.logistics),
      transaction.gameTimeMs(),
      multiplier,
      stepMs,
      valid,
    ).state as LogisticsState;
    for (const id of model.borderIds.filter((borderId) => !valid.has(borderId))) {
      const troops = state.borders[id]?.troops as TroopComposition;
      if (!troops || UNIT_IDS.every((unitId) => microFromString(troops[unitId]) === 0n)) continue;
      state = startTravel(
        state,
        id,
        "home",
        troops,
        transaction.gameTimeMs(),
        multiplier,
        stepMs,
      ) as LogisticsState;
    }
    transaction.setRecord(model.logistics, state as LogisticsState & JsonValue);
  }
}

function clearInactiveBorders(
  transaction: Transaction<Micro>,
  seats: readonly SeatModel[],
  lifecycle: MatchLifecycleState,
): void {
  for (const model of seats) {
    const combat = transaction.getRecord(model.combat);
    let changed = false;
    const borders = Object.fromEntries(
      model.borderIds.map((id) => {
        if (borderIsActive(lifecycle, model.seat.id, id)) return [id, combat.borders[id]];
        changed = true;
        transaction.setAllocation(model.targeting.id, id, 0n as Micro);
        transaction.setAllocation(model.supplyAllocation.id, id, 0n as Micro);
        transaction.setAllocation(model.shieldAllocation.id, id, 0n as Micro);
        return [
          id,
          {
            ...combat.borders[id],
            shieldCharge: "0",
            requestedTarget: "headquarters",
            effectiveTarget: "headquarters",
            lastAttack: null,
          },
        ];
      }),
    );
    if (changed) transaction.setRecord(model.combat, { borders });
  }
}

function livingSeats(
  transaction: Transaction<Micro>,
  seats: readonly SeatModel[],
): readonly string[] {
  return seats
    .filter((model) => transaction.get(model.resources.headquarters) > 0n)
    .map((model) => model.seat.id);
}
